import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Box,
  Text,
  Image,
  Container,
  VStack,
  Flex,
  Heading,
  Stack,
  Center,
  Link,
} from '@chakra-ui/react';
import { useAppSelector, useAppDispatch } from '../../app/hooks';
import { SearchScreen } from './searchScreen';
import { BtNavigate } from '../../components/BtNavigate';
import { LandingScreen } from '../landing/landingScreen';
import BtLoading from '../../components/BtLoading';
import BtFailed from '../../components/BtFailed';
import BtInput from '../../components/BtInput';
import BtDate, { today } from '../../components/BtDate';
import { images } from '../../support/imageSupport';
import BtButton from '../../components/BtButton';
import { RootState, store } from '../../app/store';
import { BOOK_SCREEN_PATH, LANDING_SCREEN_PATH } from '../../support/routeConstants';
import loadGMaps from '../../support/loadGoogle';
import BtHelmet from '../../components/BtHelmet';
import BtSpinner from '../../components/BtSpinner';
import { useSelector } from 'react-redux';

export const Search: React.FC = () => {
  const viewModel = useAppSelector((state) => state.search.viewModel);
  const logoUrl = useSelector((state: RootState) => state.gatInformation.logoUrl);
  const dispatch = useAppDispatch();
  const navigate = useNavigate();
  const [count, setCount] = useState(0);
  const [state, setState] = useState({ targetType: '' });
  const [scriptLoaded, setScriptLoaded] = useState(false);
  const [load, setLoading] = useState(false);

  useEffect(() => {
    loadGMaps(() => {
      setScriptLoaded(true);
    });
  }, []);

  useEffect(() => {
    (async () => {
      dispatch(SearchScreen.start(viewModel, logoUrl));
      setState({ targetType: '' });
    })();
  }, [store.getState().appState.state.sessionActive]);

  useEffect(() => {
    const t = setTimeout(() => {
      if (scriptLoaded) {
        if (count !== 0) {
          (async () => {
            dispatch(await SearchScreen.saveGoogleAddress(viewModel, state.targetType, logoUrl));
            setCount(0);
          })();
        }
      }
    }, 300);

    return () => clearTimeout(t);
  }, [count]);

  useEffect(() => {
    if (SearchScreen.isNone(viewModel)) {
      dispatch(SearchScreen.start(viewModel, logoUrl));
    } else if (SearchScreen.isLoading(viewModel)) {
      (async () => {
        dispatch(await SearchScreen.load(viewModel, logoUrl));
      })();
    } else if (SearchScreen.isReady(viewModel) && count === 0) {
      const element = document.getElementById('ScrollToMe');
      if (element !== null && viewModel.state.scroll) {
        const rect = element.getBoundingClientRect();
        const offsetTop = rect.top + document.body.scrollTop;
        window.scrollTo(0, offsetTop);
      }
    }

    if (!store.getState().appState.state.sessionActive && SearchScreen.isFailed(viewModel)) {
      handleNavigate(LANDING_SCREEN_PATH);
    }
  }, [viewModel]);

  const handleOnChange = async (event: React.ChangeEvent<HTMLInputElement>) => {
    event.preventDefault();

    setState({ ...state, targetType: event.target.name });
    if (event.target.value !== '') {
      setCount(count + 1);
    }
    dispatch(await SearchScreen.uppdateState(viewModel, event, logoUrl));
  };

  const handleOnChangeDate = async (event: React.ChangeEvent<HTMLInputElement>) => {
    event.preventDefault();
    dispatch(await SearchScreen.uppdateDate(viewModel, event, logoUrl));
  };

  const handleNavigate = async (path: string) => {
    if (path.length === 0) {
      navigate(-1);
    } else {
      dispatch(await LandingScreen.reset(logoUrl));
      navigate(path);
    }
  };

  const handleSearch = async () => {
    setLoading(true);
    dispatch(await SearchScreen.search(viewModel, logoUrl));
    setLoading(false);
  };

  const handleBook = async (price: string, time: string) => {
    dispatch(await SearchScreen.book(viewModel, price, time));
    navigate(BOOK_SCREEN_PATH);
  };

  const handleInterrupt = () => {
    SearchScreen.cancelFetch();
    setLoading(false);
  };

  if (SearchScreen.isLoading(viewModel)) {
    return <BtLoading {...viewModel.loading} />;
  } else if (SearchScreen.isReady(viewModel)) {
    return (
      <>
        <BtHelmet title={viewModel.metaTitle} />
        <BtSpinner
          {...viewModel.spinner}
          show={load}
          visible={true}
          onClick={() => handleInterrupt()}
        />
        <Container padding={0} size='xl'>
          <BtNavigate {...viewModel.navigation} onClick={handleNavigate} />
          <Container padding={0} maxW='container.md'>
            <Flex pb={0}>
              <VStack w='full' h='full' p={10}>
                <Heading as='h1' variant='h1' pb={5}>
                  {viewModel.greeting}
                </Heading>
                <Flex w='full' pb={50}>
                  <Stack w='full' spacing={2} direction={['column', 'column']}>
                    <BtInput {...viewModel.from} onChange={handleOnChange} />
                    <BtInput {...viewModel.to} onChange={handleOnChange} />
                    <BtDate {...viewModel.date} onChange={handleOnChangeDate} />
                    {viewModel.date.message && today() === viewModel.date.value && (
                      <Stack spacing={5} direction={['row', 'row']} py={2} alignItems='center'>
                        <Image src={images(viewModel.ikon)} />
                        <Link href={`tel:+${viewModel.phone}`} color='brand.500'>
                          {viewModel.phone}
                        </Link>
                      </Stack>
                    )}
                    <Flex pb={4}></Flex>
                    <BtButton
                      variant='primary'
                      size='lg'
                      w='full'
                      {...viewModel.search}
                      action={() => handleSearch()}
                    />
                  </Stack>
                </Flex>
              </VStack>
            </Flex>
          </Container>
          <Container padding={0} maxW='100%' bg='darkgray.100' height='100vh'>
            <Container padding={0} maxW='container.md'>
              <Flex pb={200}>
                <VStack w='full' h='full' p={10}>
                  <Flex w='full' pb={150} id='ScrollToMe'>
                    {viewModel.state.slots.length === 0 && (
                      <Box w='100%' p={50} bg='black' borderRadius='lg' boxShadow='brand'>
                        <Stack w='full' spacing={4} direction={['column', 'column']}>
                          <Center>
                            <Image src={images(viewModel.image)} alt={''} />
                          </Center>
                          <Center>
                            <Text as='h4' fontSize='xl' align='center'>
                              {viewModel.info}
                            </Text>
                          </Center>
                        </Stack>
                      </Box>
                    )}
                    {viewModel.state.slots.length > 0 && (
                      <Stack w='full' spacing={4} direction={['column', 'column']}>
                        <Text as='h4' fontSize='xl'>
                          {viewModel.body}
                        </Text>

                        {viewModel.state.slots.map((slot, index) => {
                          return (
                            <Flex key={`${index}-${slot.price}`}>
                              <Box
                                w='100%'
                                p={5}
                                border='1px solid rgba(255, 212, 163, 0.2)'
                                borderRadius='lg'
                              >
                                <Stack
                                  w='full'
                                  spacing={4}
                                  direction={['column', 'row']}
                                  justify='space-between'
                                >
                                  <Center>
                                    <Image pr={5} src={images(viewModel.time)} />
                                    <VStack w='full' py={[2, 8]} alignItems='start'>
                                      <Text>{viewModel.pickUp}</Text>
                                      <Text as='h4' fontSize='xl' m={'0 !important'}>
                                        {slot.startTime} - {slot.endTime}
                                      </Text>
                                    </VStack>
                                  </Center>

                                  <Center>
                                    <Image pr={5} src={images(viewModel.price)} />
                                    <Stack w='full' py={5} direction={['row', 'column']}>
                                      <Text as='h4' fontSize='xl'>
                                        {slot.price}
                                      </Text>
                                    </Stack>
                                  </Center>
                                </Stack>

                                <BtButton
                                  variant='primary'
                                  size='lg'
                                  w='full'
                                  {...viewModel.book}
                                  action={() => handleBook(slot.price, slot.slotTime)}
                                />
                              </Box>
                            </Flex>
                          );
                        })}
                      </Stack>
                    )}
                  </Flex>
                </VStack>
              </Flex>
            </Container>
          </Container>
        </Container>
      </>
    );
  } else if (SearchScreen.isFailed(viewModel)) {
    return <BtFailed {...viewModel.error} />;
  } else {
    return <BtLoading {...viewModel.loading} />;
  }
};
